"use client";

import {
  getCategories,
  getProductsByCategoryId,
} from "@frontend/lib/actions/actions";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { CategorySection } from "./CategorySection";
import { CategorySelector } from "./CategorySelector";

export function CategoryListClient({
  params,
  props,
}: {
  params: { clientSlug: string; orgId: number };
  props?: {
    scrollId: string;
    priceToggleColor: string;
    displayMode: "default" | "grid" | "accordion";
    showSelector?: boolean;
    selectorTitle?: string;
  };
}) {
  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", params.orgId],
    queryFn: () => getCategories(params.orgId),
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      data
        .sort((a, b) => b.popularity - a.popularity)
        .filter((category) => category.name !== "Featured"),
  });

  // Fetch products based on categories
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["products", categories?.map((c) => c.id)],
    queryFn: () =>
      getProductsByCategoryId(categories?.map((category) => category.id) || []),
    enabled: !!categories?.length,
    staleTime: 5 * 60 * 1000,
  });

  // Loading state
  if (categoriesLoading || productsLoading) {
    return (
      <div className="w-full py-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // No categories or products
  if (!categories?.length || !products) {
    return null;
  }

  // Filter out products where the hrts are not active
  const productsWithActiveHrts = products.filter((product) => {
    return product.productToHrt.some((hrt) => hrt.hrt.isConsumerFacing);
  });

  const groupedProductsByCategory = productsWithActiveHrts.reduce(
    (acc, product) => {
      // Iterate through all category IDs for the product
      for (const categoryId of product.categoryIds) {
        if (!acc[categoryId]) {
          acc[categoryId] = [];
        }
        acc[categoryId].push(product);
      }
      return acc;
    },
    {} as Record<string, typeof productsWithActiveHrts>,
  );

  // filter out categories with no products
  const categoriesWithProducts = categories.filter(
    (category) => groupedProductsByCategory[category.id]?.length > 0,
  );

  return (
    <div className="flex flex-col gap-0" id={props?.scrollId}>
      {props?.showSelector && (
        <CategorySelector
          title={props?.selectorTitle}
          categoriesWithProducts={categoriesWithProducts}
        />
      )}

      {categoriesWithProducts.map((category, index) => (
        <CategorySection
          key={category.id}
          category={{ id: category.id.toString(), name: category.name }}
          products={groupedProductsByCategory[category.id]}
          clientSlug={params.clientSlug}
          orgId={params.orgId}
          priceToggleColor={props?.priceToggleColor}
          displayMode={props?.displayMode || "default"}
          languageCode="en"
        />
      ))}
    </div>
  );
}
